/**
 * useRoleProfile (web) — single source of truth for the worker's role
 * archetype, mirroring svdy-mobile/src/hooks/useRoleProfile.ts.
 *
 * The worker portal is composed by TWO dimensions, never a coarse role_code:
 *   A. Do they punch?   → profile.attendance_tracked          (tracked)
 *   B. Do they oversee? → manager-overview scope/is_manager/   (oversight)
 *                         team_size/can_review
 *
 * From those we derive `oversight` (none | team | org) and an `archetype`
 * (R1 IC worker / R2 team lead / R3 admin). This fixes the web bug where a
 * `role_code='worker'` who manages a team saw NO Team/Approvals (the old
 * useRoleLanding.isManager only matched role_code ∈ manager/hr_manager/admin).
 * Now web gates by ACTUAL oversight — identical to mobile.
 *
 * Backend semantics (app/me/service.py get_manager_overview):
 *   - scope == 'organization' iff admin-tier (super_admin/admin/hr_manager).
 *   - is_manager = is_admin OR team_size>0 OR has pending approval work.
 *   - can_review = holds attendance.approvals.read.
 *
 * Single fetch shared across consumers via a module-level cache, so the
 * layout + dashboard + screens don't each re-fetch.
 */
import { useEffect, useState } from 'react';

import { meApi } from '../api';
import type { MeProfile, MeManagerOverview, DashboardResponse } from '../api/me';

export type Oversight = 'none' | 'team' | 'org';
export type Archetype = 'R1' | 'R2' | 'R3';

export interface RoleProfile {
  tracked: boolean;
  oversight: Oversight;
  canReview: boolean;
  /** ANY attendance-approval authority: the org-wide permission, or being the routed approver of at
   *  least one pending request. The To-do entry gates on this, not on `canReview` (2026-09-07). */
  attendanceReviewer: boolean;
  /** The same, for the FACE queue (2026-09-08). */
  faceReviewer: boolean;
  scope: string;
  teamSize: number;
  isManager: boolean;
  archetype: Archetype;
  pending: { approvals: number; attendance: number; faceReviews: number };
  licensedProducts: string[];
  hasQutime: boolean;
  hasPureoa: boolean;
  profile: MeProfile | null;
  manager: MeManagerOverview | null;
}

/** Pure derivation — exported for testability against the 3 archetypes. */
export function deriveRole(
  profile: MeProfile | null,
  mgr: MeManagerOverview | null,
  // NO optimistic default. This was `['qutime']`, so every caller that omitted
  // the argument — and every failed dashboard fetch — reported the org as holding
  // QuTime. A PureOA-only worker was shown Attendance, Face matching and the face
  // enrolment banner on that basis. Unknown now means unlicensed, and the route
  // boundaries hold a spinner rather than rendering while it is unknown, so a
  // QuTime worker never sees the deny.
  licensedProducts: string[] = [],
): RoleProfile {
  const tracked = profile?.attendance_tracked !== false;
  const scope = mgr?.scope ?? 'team';
  const isManager = !!mgr?.is_manager;
  const teamSize = mgr?.team_size ?? 0;
  const canReview = !!mgr?.can_review;
  // Routed authority; `?? canReview` fails in the safe direction against an older backend.
  const attendanceReviewer = mgr?.attendance_reviewer ?? canReview;
  const faceReviewer = mgr?.face_reviewer ?? canReview;
  /**
   * WHETHER SOMEONE HAS A TEAM IS AN EMPLOYMENT FACT, NOT AN ATTENDANCE ONE (2026-09-07).
   *
   * `team_size` counts attendance-TRACKED reports, which is right for the ring's denominator and wrong
   * as the authority signal: on a tenant where nobody punches — the normal state of a PureOA-only
   * tenant — a manager with real reports read as `oversight: 'none'` and lost the Team button here and
   * the Team tab on mobile, while `/me/process-summary` resolved the same caller to `scope: 'team'`.
   * Measured before the fix: 3 employed reports, `team_size` 0, `oversight` 'none'.
   *
   * Falls back to `team_size` so a client running against an older backend degrades to the previous
   * behaviour instead of to "nobody has a team".
   */
  const reportsCount = mgr?.reports_count ?? teamSize;

  let oversight: Oversight = 'none';
  if (scope === 'organization') oversight = 'org';
  else if (isManager && reportsCount > 0) oversight = 'team';

  const archetype: Archetype =
    oversight === 'org' ? 'R3' : oversight === 'team' ? 'R2' : 'R1';

  // Verbatim. The previous `licensedProducts.length ? licensedProducts : ['qutime']`
  // rewrote the server's honest "no products" into "QuTime", which is exactly the
  // answer a lapsed or PureOA-only tenant must not get.
  const lp = Array.isArray(licensedProducts) ? licensedProducts : [];

  return {
    tracked,
    oversight,
    canReview,
    attendanceReviewer,
    faceReviewer,
    scope,
    teamSize,
    isManager,
    archetype,
    pending: {
      approvals: mgr?.pending_approvals ?? 0,
      attendance: mgr?.pending_attendance ?? 0,
      faceReviews: mgr?.pending_face_reviews ?? 0,
    },
    licensedProducts: lp,
    hasQutime: lp.includes('qutime'),
    hasPureoa: lp.includes('pureoa'),
    profile,
    manager: mgr,
  };
}

// --------------------------------------------------------------------------
// Module-level store — single source shared across all consumers.
// --------------------------------------------------------------------------

let cache: RoleProfile | null = null;
let inflight: Promise<RoleProfile> | null = null;
const subscribers = new Set<(r: RoleProfile) => void>();

async function fetchRole(): Promise<RoleProfile> {
  const [profile, mgr, dash] = await Promise.all([
    meApi.getProfile().catch(() => null),
    meApi.getManagerOverview().catch(() => null),
    meApi.getDashboard().catch(() => null),
  ]);
  // A failed /me/dashboard yields NO products, not QuTime. `?? []` rather than
  // `?.length ? … : ['qutime']`: the fetch above already swallows the error into
  // `null`, so this branch is exactly the "we could not find out" case, and
  // guessing there is what put a face-enrolment prompt in front of PureOA-only
  // workers.
  const lp = (dash as DashboardResponse | null)?.licensed_products ?? [];
  const role = deriveRole(profile as MeProfile | null, mgr as MeManagerOverview | null, lp);
  cache = role;
  subscribers.forEach((fn) => fn(role));
  return role;
}

export function refreshRoleProfile(): Promise<RoleProfile> {
  inflight = fetchRole().finally(() => { inflight = null; });
  return inflight;
}

export function clearRoleProfile(): void {
  cache = null;
  inflight = null;
}

export interface UseRoleProfileResult {
  role: RoleProfile | null;
  loading: boolean;
  refresh: () => Promise<RoleProfile>;
}

export function useRoleProfile(): UseRoleProfileResult {
  const [role, setRole] = useState<RoleProfile | null>(cache);
  const [loading, setLoading] = useState<boolean>(cache === null);

  useEffect(() => {
    let active = true;
    const sub = (r: RoleProfile) => {
      if (active) { setRole(r); setLoading(false); }
    };
    subscribers.add(sub);

    if (cache !== null) {
      setRole(cache);
      setLoading(false);
    } else {
      setLoading(true);
      (inflight ?? refreshRoleProfile()).catch(() => { if (active) setLoading(false); });
    }

    return () => { active = false; subscribers.delete(sub); };
  }, []);

  return { role, loading, refresh: refreshRoleProfile };
}
